import { Loader } from "@mantine/core"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import { CountryIso3166 } from "@tfg/db"
import { ActionIcon, Missing } from "@tfg/ui"
import { Dispatch, SetStateAction, useMemo } from "react"

import { CountryCell } from "~/components/cells/CountryCell"
import {
  CreateSupplierMappingRequestDto,
  FindSupplierMappingResponseDto,
  UpdateSupplierMappingRequestDto,
} from "~/gen/api/types"
import { useDeleteSupplierMappingMutation } from "~/pages/Brand/OrganizationSettings/Suppliers/hooks/useDeleteSupplierMappingMutation"

function constructSupplierAddress(
  street: string | null,
  city: string | null,
  postalCode: string | null
) {
  if (
    !street ||
    !city ||
    street === "not-entered-yet" ||
    city === "not-entered-yet"
  ) {
    return <Missing />
  }
  const addressParts = [street, city]
  if (postalCode) {
    addressParts.push(postalCode)
  }
  const address = addressParts.join(", ")
  return address.endsWith(",") ? address.slice(0, -1) : address
}

export const useColumns = ({
  setInitialValues,
  setValuesUpdated,
  setFormType,
  searchSupplier,
}: {
  setInitialValues: Dispatch<
    SetStateAction<
      CreateSupplierMappingRequestDto | UpdateSupplierMappingRequestDto
    >
  >
  setValuesUpdated: Dispatch<SetStateAction<boolean>>
  setFormType: Dispatch<SetStateAction<"create" | "update">>
  searchSupplier: string
}) => {
  const columnHelper = createColumnHelper<FindSupplierMappingResponseDto>()
  const { mutate: deleteSupplierMapping, isLoading } =
    useDeleteSupplierMappingMutation({ searchSupplier })

  return useMemo<ColumnDef<FindSupplierMappingResponseDto, any>[]>(() => {
    const update = async (values: FindSupplierMappingResponseDto) => {
      setInitialValues((prev) => ({
        ...prev,
        id: values.id,
        supplierId: values.supplier.id,
        name: values.supplier.name,
        email: values.supplier.email,
        company: {
          id: values.supplier.company.id,
          name: values.supplier.company.name,
          street:
            values.supplier.company.street !== "not-entered-yet"
              ? (values.supplier.company.street ?? "")
              : "",
          city:
            values.supplier.company.city !== "not-entered-yet"
              ? (values.supplier.company.city ?? "")
              : "",
          postalCode: values.supplier.company.postalCode ?? "",
          country: values.supplier.company.country ?? CountryIso3166.FR,
        },
      }))
      setValuesUpdated(true)
      setFormType("update")
    }

    return [
      columnHelper.accessor("supplier.name", {
        header: "Supplier",
        cell: ({ getValue }) => getValue(),
      }),
      columnHelper.accessor("supplier.email", {
        header: "Email",
        id: "email",
        cell: ({ getValue }) => getValue(),
      }),
      columnHelper.accessor("supplier.company.name", {
        header: "Company",
        enableSorting: false,
        cell: ({ getValue }) => getValue(),
      }),
      columnHelper.accessor(
        (originalRow) =>
          constructSupplierAddress(
            originalRow.supplier.company.street,
            originalRow.supplier.company.city,
            originalRow.supplier.company.postalCode
          ),
        {
          header: "Address",
          enableSorting: false,
          cell: ({ getValue }) => getValue(),
        }
      ),

      columnHelper.accessor("supplier.company.country", {
        header: "Country",
        enableSorting: false,
        cell: (info) => {
          return <CountryCell countryIso3166={info.getValue()} />
        },
      }),
      columnHelper.display({
        header: undefined,
        id: "delete",
        enableSorting: false,
        cell: ({ row: { original } }) =>
          !isLoading ? (
            <ActionIcon
              type="delete"
              onClick={() =>
                deleteSupplierMapping({ supplierMappingId: original.id })
              }
            />
          ) : (
            <Loader />
          ),
      }),
      columnHelper.display({
        id: "edit",
        header: undefined,
        cell: ({ row: { original } }) => {
          return <ActionIcon type="edit" onClick={() => update(original)} />
        },
      }),
    ]
  }, [
    columnHelper,
    deleteSupplierMapping,
    isLoading,
    setFormType,
    setInitialValues,
    setValuesUpdated,
  ])
}
